import { ArrowRight, ChevronRight, Eye, GitCommitHorizontal, Save } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { apiErrorMessage } from '@/i18n'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { HrSurface } from '@/features/hr/personel/components/HrSurface'
import { TemplateEditorWorkspace } from './components/TemplateEditorWorkspace'
import { TemplatePreview } from './components/TemplatePreview'
import { ensureLocalDraft, saveTemplateDraft, useTemplateBundle } from './api'
import type { SablonSurum } from './types'

export function SablonDuzenleyiciPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const bundle = useTemplateBundle(id)
  const sablon = bundle.data?.sablon
  const surumler = bundle.data?.surumler ?? []
  const hydrated = useRef<string | null>(null)
  const [draft, setDraft] = useState<SablonSurum | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    if (!id || !bundle.data || hydrated.current === id) return
    hydrated.current = id
    setDraft(ensureLocalDraft(bundle.data.surumler, bundle.data.sablon))
    setDirty(false)
  }, [bundle.data, id])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const published = useMemo(
    () => surumler.find((s) => s.status === 'yayinda'),
    [surumler],
  )

  async function save() {
    if (!draft) return
    setSaving(true)
    try {
      await saveTemplateDraft(draft)
      setDirty(false)
      setLastSaved(new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }))
      toast.success(t('hr.belgeler.toasts.draftSaved'))
    } catch (error) {
      toast.error(apiErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  function onChange(next: SablonSurum) {
    setDraft(next)
    setDirty(true)
  }

  if (bundle.isError || (bundle.isFetched && !sablon)) {
    return (
      <HrSurface>
        <ErrorState error={new Error('not_found')} onRetry={() => navigate('/hr/belgeler')} />
      </HrSurface>
    )
  }

  if (!sablon || !draft) {
    return (
      <HrSurface>
        <div className="py-20 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      </HrSurface>
    )
  }

  async function leave() {
    if (dirty) {
      const ok = await confirmModal({
        title: t('hr.belgeler.unsavedTitle'),
        description: t('hr.belgeler.unsavedConfirm'),
        destructive: true,
        confirmLabel: t('common.confirm'),
      })
      if (!ok) return
    }
    navigate('/hr/belgeler')
  }

  const versionLabel = `v${published?.version ?? sablon.currentVersion} → v${draft.version}`

  return (
    <HrSurface density="editor">
      <PageHeader
        backTo="/hr/belgeler"
        onBack={() => void leave()}
        eyebrow={
          <nav aria-label={t('hr.belgeler.listTitle')} className="flex items-center gap-1">
            <Link to="/hr/belgeler" className="hover:text-foreground">
              {t('hr.belgeler.listTitle')}
            </Link>
            <ChevronRight className="size-3" />
            <span className="truncate text-foreground">{sablon.ad}</span>
          </nav>
        }
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {t('hr.belgeler.editorTitle', { name: sablon.ad })}
            <Badge tone="info">{t('hr.belgeler.status.taslak')}</Badge>
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1 font-medium tabular-nums text-foreground">
              <GitCommitHorizontal className="size-3.5 text-primary" />
              {versionLabel}
            </span>
            <span aria-hidden className="opacity-40">•</span>
            <span>{lastSaved ? t('hr.belgeler.editor.lastSaved', { time: lastSaved }) : t('hr.belgeler.editor.autosaveHint')}</span>
            {dirty ? <Badge tone="warning">{t('hr.belgeler.editor.unsaved')}</Badge> : null}
          </span>
        }
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
              <Eye />
              {t('hr.belgeler.previewAction')}
            </Button>
            <Button type="button" variant="outline" onClick={save} disabled={!dirty || saving}>
              <Save />
              {t('hr.belgeler.saveTemplate')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (dirty) void save()
                navigate(`/hr/belgeler/sablonlar/${sablon.id}/surum`)
              }}
            >
              {t('hr.belgeler.editor.continuePublish')}
              <ArrowRight />
            </Button>
          </>
        }
      />

      {draft.method === 'pdf' ? <EmptyState title={t('hr.belgeler.editor.pdfReadonly')} /> : <TemplateEditorWorkspace surum={draft} onChange={onChange} />}

      <Modal open={previewOpen} onOpenChange={setPreviewOpen} icon={<Eye />} title={t('hr.belgeler.previewAction')} size="xl" bodyClassName="overflow-auto scrollbar-none p-0">
        <TemplatePreview surum={draft} />
      </Modal>
    </HrSurface>
  )
}
